/**
 * Pure game engine for Who Wants to Be a Millionaire (single contestant).
 */
import {
  CHOICE_LETTERS,
  DEFAULT_MODIFIERS,
  MAX_PLAYER_AGE,
  MIN_PLAYER_AGE,
  PHONE_FRIEND_SECONDS,
  QUESTION_COUNT,
  TIMER_SECONDS_BY_INDEX
} from "./millionaireConstants";
import {
  buildLadder,
  dropAmount,
  walkAwayAmount,
  winningsAfterCorrect
} from "./millionaireLadder";

/**
 * @typedef {{
 *   n: number,
 *   prompt: string,
 *   choices: string[],
 *   correctIndex: number,
 *   category?: string,
 *   explanation?: string
 * }} MillionaireQuestion
 */

/**
 * @typedef {{
 *   name: string,
 *   age: number
 * }} MillionairePlayer
 */

/**
 * @typedef {{
 *   status: "playing"|"won"|"wrong"|"walked"|"timeout",
 *   player: MillionairePlayer,
 *   questions: MillionaireQuestion[],
 *   ladder: { amounts: number[], safeIndices: number[], topPrize: number },
 *   modifiers: typeof DEFAULT_MODIFIERS,
 *   questionIndex: number,
 *   clearedThrough: number,
 *   currentWinnings: number,
 *   pendingChoice: number|null,
 *   lockedChoice: number|null,
 *   revealCorrect: boolean,
 *   removedChoices: number[],
 *   audiencePercents: number[]|null,
 *   lifelines: { fiftyFifty: boolean, phoneFriend: boolean, audience: boolean },
 *   phoneFriendActive: boolean,
 *   phoneFriendCounting: boolean,
 *   phoneFriendSecondsLeft: number,
 *   timerSecondsLeft: number|null,
 *   timerPaused: boolean,
 *   finalAmount: number,
 *   hostCaption: string
 * }} MillionaireGameState
 */

function enabledLifelines(modifiers) {
  return {
    fiftyFifty: !modifiers.disableFiftyFifty,
    phoneFriend: !modifiers.disablePhoneFriend,
    audience: !modifiers.disableAudience
  };
}

function timerForIndex(index, modifiers) {
  if (modifiers.unlimitedTime) return null;
  return TIMER_SECONDS_BY_INDEX[index] ?? 60;
}

/**
 * @param {{
 *   player: MillionairePlayer,
 *   questions: MillionaireQuestion[],
 *   topPrize?: number,
 *   ladder?: ReturnType<typeof buildLadder>,
 *   modifiers?: Partial<typeof DEFAULT_MODIFIERS>
 * }} opts
 * @returns {MillionaireGameState}
 */
export function createGame({
  player,
  questions,
  topPrize,
  ladder,
  modifiers = {}
}) {
  const mods = { ...DEFAULT_MODIFIERS, ...modifiers };
  const built = ladder || buildLadder(topPrize);
  const qs = Array.isArray(questions) ? questions.slice(0, QUESTION_COUNT) : [];
  if (qs.length < QUESTION_COUNT) {
    throw new Error(`Need ${QUESTION_COUNT} questions, got ${qs.length}`);
  }

  const lifeEnabled = enabledLifelines(mods);

  return {
    status: "playing",
    player: {
      name: String(player?.name || "Player").trim() || "Player",
      age: Math.max(MIN_PLAYER_AGE, Math.min(MAX_PLAYER_AGE, Number(player?.age) || 18))
    },
    questions: qs,
    ladder: built,
    modifiers: mods,
    questionIndex: 0,
    clearedThrough: -1,
    currentWinnings: 0,
    pendingChoice: null,
    lockedChoice: null,
    revealCorrect: false,
    removedChoices: [],
    audiencePercents: null,
    lifelines: {
      fiftyFifty: lifeEnabled.fiftyFifty,
      phoneFriend: lifeEnabled.phoneFriend,
      audience: lifeEnabled.audience
    },
    phoneFriendActive: false,
    phoneFriendCounting: false,
    phoneFriendSecondsLeft: 0,
    timerSecondsLeft: timerForIndex(0, mods),
    timerPaused: false,
    finalAmount: 0,
    hostCaption: "Let's play!"
  };
}

export function currentQuestion(state) {
  return state.questions[state.questionIndex] || null;
}

export function selectAnswer(state, choiceIndex) {
  if (state.status !== "playing") return state;
  if (state.revealCorrect || state.lockedChoice != null) return state;
  if (state.phoneFriendActive) return state;
  const idx = Number(choiceIndex);
  if (!Number.isInteger(idx) || idx < 0 || idx > 3) return state;
  if (state.removedChoices.includes(idx)) return state;

  return {
    ...state,
    pendingChoice: idx,
    timerPaused: true,
    hostCaption: "Is that your final answer?"
  };
}

export function cancelPending(state) {
  if (state.status !== "playing") return state;
  if (state.lockedChoice != null) return state;
  return {
    ...state,
    pendingChoice: null,
    timerPaused: state.phoneFriendActive,
    hostCaption: "Take your time…"
  };
}

/**
 * Lock in pending answer. Returns state after reveal (correct advances on nextQuestion).
 */
export function confirmFinalAnswer(state) {
  if (state.status !== "playing") return state;
  if (state.pendingChoice == null) return state;

  const q = currentQuestion(state);
  const locked = state.pendingChoice;
  const correct = locked === q.correctIndex;
  const amounts = state.ladder.amounts;

  if (correct) {
    const winnings = winningsAfterCorrect(amounts, state.questionIndex);
    const isLast = state.questionIndex >= QUESTION_COUNT - 1;
    return {
      ...state,
      pendingChoice: null,
      lockedChoice: locked,
      revealCorrect: true,
      currentWinnings: winnings,
      clearedThrough: state.questionIndex,
      timerPaused: true,
      hostCaption: isLast
        ? "You've just become a millionaire!"
        : "Correct! Moving on…",
      ...(isLast
        ? { status: "won", finalAmount: winnings }
        : {})
    };
  }

  const drop = dropAmount(amounts, state.ladder.safeIndices, state.questionIndex);
  return {
    ...state,
    pendingChoice: null,
    lockedChoice: locked,
    revealCorrect: true,
    currentWinnings: drop,
    finalAmount: drop,
    status: "wrong",
    timerPaused: true,
    hostCaption: "Oh, I'm sorry — that's not correct."
  };
}

/**
 * Advance to next question after a correct reveal (not used on final win).
 */
export function advanceAfterCorrect(state) {
  if (state.status !== "playing") return state;
  if (!state.revealCorrect || state.lockedChoice == null) return state;
  const q = currentQuestion(state);
  if (state.lockedChoice !== q.correctIndex) return state;
  if (state.questionIndex >= QUESTION_COUNT - 1) return state;

  const nextIndex = state.questionIndex + 1;
  return {
    ...state,
    questionIndex: nextIndex,
    pendingChoice: null,
    lockedChoice: null,
    revealCorrect: false,
    removedChoices: [],
    audiencePercents: null,
    phoneFriendActive: false,
    phoneFriendCounting: false,
    phoneFriendSecondsLeft: 0,
    timerSecondsLeft: timerForIndex(nextIndex, state.modifiers),
    timerPaused: false,
    hostCaption: `Question ${nextIndex + 1} for ${formatQuick(state.ladder.amounts[nextIndex])}`
  };
}

function formatQuick(n) {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: n < 1 ? 2 : 0
    }).format(n);
  } catch {
    return `$${n}`;
  }
}

export function walkAway(state) {
  if (state.status !== "playing") return state;
  const amount = state.revealCorrect
    ? state.currentWinnings
    : walkAwayAmount(state.ladder.amounts, state.questionIndex);
  return {
    ...state,
    status: "walked",
    pendingChoice: null,
    finalAmount: amount,
    currentWinnings: amount,
    timerPaused: true,
    hostCaption: "You've decided to walk away."
  };
}

export function applyFiftyFifty(state) {
  if (state.status !== "playing") return state;
  if (!state.lifelines.fiftyFifty) return state;
  if (state.revealCorrect || state.pendingChoice != null) return state;

  const q = currentQuestion(state);
  const wrong = [0, 1, 2, 3].filter((i) => i !== q.correctIndex);
  // Shuffle and pick 2 to remove
  for (let i = wrong.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [wrong[i], wrong[j]] = [wrong[j], wrong[i]];
  }
  const removed = wrong.slice(0, 2);

  return {
    ...state,
    lifelines: { ...state.lifelines, fiftyFifty: false },
    removedChoices: removed,
    audiencePercents: null,
    hostCaption: "Fifty-fifty — two answers remain."
  };
}

/**
 * Simulate audience poll: bias toward correct, flatter on harder questions.
 */
export function applyAudience(state) {
  if (state.status !== "playing") return state;
  if (!state.lifelines.audience) return state;
  if (state.revealCorrect || state.pendingChoice != null) return state;

  const q = currentQuestion(state);
  const available = [0, 1, 2, 3].filter((i) => !state.removedChoices.includes(i));
  const qi = state.questionIndex;

  // Confidence of audience drops as questions get harder
  const correctBias = Math.max(0.28, 0.82 - qi * 0.035);
  const percents = [0, 0, 0, 0];
  let remaining = 100;

  // Assign correct first among available
  if (available.includes(q.correctIndex)) {
    const correctShare = Math.round(
      (correctBias + (Math.random() - 0.5) * 0.12) * 100
    );
    percents[q.correctIndex] = Math.max(15, Math.min(85, correctShare));
    remaining -= percents[q.correctIndex];
  }

  const others = available.filter((i) => i !== q.correctIndex);
  others.forEach((idx, n) => {
    if (n === others.length - 1) {
      percents[idx] = Math.max(0, remaining);
    } else {
      const share = Math.round((remaining / (others.length - n)) * (0.4 + Math.random() * 0.8));
      percents[idx] = Math.max(0, Math.min(remaining, share));
      remaining -= percents[idx];
    }
  });

  // Normalize to 100 among available
  const sum = available.reduce((s, i) => s + percents[i], 0);
  if (sum !== 100 && sum > 0) {
    const diff = 100 - sum;
    percents[available[0]] += diff;
  }

  return {
    ...state,
    lifelines: { ...state.lifelines, audience: false },
    audiencePercents: percents,
    hostCaption: "Let's ask the audience…"
  };
}

export function startPhoneFriend(state) {
  if (state.status !== "playing") return state;
  if (!state.lifelines.phoneFriend) return state;
  if (state.revealCorrect || state.pendingChoice != null) return state;

  return {
    ...state,
    lifelines: { ...state.lifelines, phoneFriend: false },
    phoneFriendActive: true,
    phoneFriendCounting: false,
    phoneFriendSecondsLeft: PHONE_FRIEND_SECONDS,
    timerPaused: true,
    hostCaption: "Phone-a-Friend — get them on the line!"
  };
}

export function beginPhoneFriendTimer(state) {
  if (!state.phoneFriendActive || state.phoneFriendCounting) return state;
  return {
    ...state,
    phoneFriendCounting: true,
    hostCaption: "Phone-a-Friend — the clock is running!"
  };
}

export function endPhoneFriend(state) {
  if (!state.phoneFriendActive) return state;
  return {
    ...state,
    phoneFriendActive: false,
    phoneFriendCounting: false,
    phoneFriendSecondsLeft: 0,
    timerPaused: state.pendingChoice != null,
    hostCaption: "Back to you…"
  };
}

export function tickPhoneFriend(state) {
  if (!state.phoneFriendActive || !state.phoneFriendCounting) return state;
  const left = (state.phoneFriendSecondsLeft || 0) - 1;
  if (left <= 0) return endPhoneFriend(state);
  return { ...state, phoneFriendSecondsLeft: left };
}

export function tickTimer(state) {
  if (state.status !== "playing") return state;
  if (state.modifiers.unlimitedTime) return state;
  if (state.timerPaused || state.timerSecondsLeft == null) return state;
  if (state.revealCorrect) return state;

  const left = state.timerSecondsLeft - 1;
  if (left > 0) return { ...state, timerSecondsLeft: left };

  // Time's up = wrong
  const drop = dropAmount(
    state.ladder.amounts,
    state.ladder.safeIndices,
    state.questionIndex
  );
  return {
    ...state,
    status: "timeout",
    timerSecondsLeft: 0,
    timerPaused: true,
    revealCorrect: true,
    lockedChoice: null,
    pendingChoice: null,
    currentWinnings: drop,
    finalAmount: drop,
    hostCaption: "Time's up!"
  };
}

export function pauseTimer(state) {
  return { ...state, timerPaused: true };
}

export function resumeTimer(state) {
  if (state.status !== "playing") return state;
  if (state.pendingChoice != null || state.phoneFriendActive || state.revealCorrect) {
    return state;
  }
  return { ...state, timerPaused: false };
}

export function choiceLetter(index) {
  return CHOICE_LETTERS[index] || "?";
}
